import {
  ImageScaleView,
  type ImageScaleViewOpts,
} from '@play-co/timestep-core/ui';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';

/** Shared icon image displayed for each selectable Dream Drift hero. */
const HERO_ICON_IMAGES: Record<DreamDriftHero, string> = {
  rumbleHero: 'assets/ui/dreamDrift/common/icon_rumble.png',
  whirlwindDancer: 'assets/ui/dreamDrift/common/icon_dancer.png',
  skySummoner: 'assets/ui/dreamDrift/common/icon_summoner.png',
  prismPainter: 'assets/ui/dreamDrift/common/icon_painter.png',
};

/** Layout options accepted by the fixed-size Dream Drift hero icon. */
type DreamDriftHeroIconOpts = Omit<
  ImageScaleViewOpts,
  'centerOnOrigin' | 'height' | 'image' | 'scaleMethod' | 'visible' | 'width'
>;

/** Badge displaying the hero selected for the current Dream Drift segment. */
export default class DreamDriftHeroIcon extends ImageScaleView {
  /**
   * Create a hidden hero icon at the requested parent and position.
   *
   * @param opts Parent, position, and other base image view options.
   */
  constructor(opts: DreamDriftHeroIconOpts = {}) {
    // Preserve the source asset proportions while centering the badge on its position.
    super({
      ...opts,
      width: 72,
      height: 75,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      visible: false,
    });
  }

  /**
   * Display the requested hero image or hide the badge when none is selected.
   *
   * @param opts Currently selected Dream Drift hero, when available.
   * @returns Nothing.
   */
  public setup({ hero }: { hero?: DreamDriftHero }): void {
    // Missing selections should not leave a stale hero badge visible.
    if (!hero) {
      this.hide();
      return;
    }

    // Display the icon mapped to the current validated hero selection.
    this.setImage(HERO_ICON_IMAGES[hero]);
    this.show();
  }
}
